/* Inline quick-add size strip on product cards (desktop).
   Reveals on photo hover; stacked option rows (Top / Bottom) + a ring add button beside them:
   the ring fills as required sizes are picked, then morphs into a black "+ price" pill when ready.
   Adds via /cart/add.js and refreshes the cart drawer silently (no drawer open).
   Oct 8 2026: no size is preselected; the add button stays disabled until the shopper picks.
   Oct 13 2026: "Buy now" under the sizes follows the same rule (off until sizes are picked);
   it adds the picked size and goes straight to checkout. */
(function () {
  'use strict';
  var RESET_DELAY = 3200;
  var ADDED_DELAY = 3000;

  function getData(strip) {
    if (!strip.__qsData) {
      var parsed = {variants: []};
      try {
        parsed = JSON.parse(strip.querySelector('[data-strip-data]').textContent);
      } catch (e) {
        // leave empty
      }
      strip.__qsData = parsed;
    }
    return strip.__qsData;
  }

  function optValue(variant, i) {
    return i === 0 ? variant.o1 : variant.o2;
  }

  function buttons(strip, i) {
    return Array.prototype.slice.call(strip.querySelectorAll('[data-strip-opt="' + i + '"]'));
  }

  function selectedBtn(strip, i) {
    return strip.querySelector('[data-strip-opt="' + i + '"].is-selected');
  }

  function hasSecondRow(strip) {
    return !!strip.querySelector('[data-strip-opt="1"]');
  }

  function refreshAvailability(strip) {
    var data = getData(strip);
    [0, 1].forEach(function (i) {
      var btns = buttons(strip, i);
      if (!btns.length) return;
      var other = 1 - i;
      var otherSel = selectedBtn(strip, other);
      var otherValue = otherSel ? otherSel.getAttribute('data-strip-value') : null;
      btns.forEach(function (b) {
        var value = b.getAttribute('data-strip-value');
        var ok = data.variants.some(function (v) {
          return v.a && optValue(v, i) === value && (!otherValue || optValue(v, other) === otherValue);
        });
        b.disabled = !ok;
        if (!ok) b.classList.remove('is-selected');
      });
    });
  }

  function readyVariant(strip) {
    var data = getData(strip);
    var s0 = selectedBtn(strip, 0);
    if (!s0) return null;
    var v0 = s0.getAttribute('data-strip-value');
    if (!hasSecondRow(strip)) {
      return data.variants.filter(function (v) {
        return v.o1 === v0 && v.a;
      })[0] || null;
    }
    var s1 = selectedBtn(strip, 1);
    if (!s1) return null;
    var v1 = s1.getAttribute('data-strip-value');
    return data.variants.filter(function (v) {
      return v.o1 === v0 && v.o2 === v1 && v.a;
    })[0] || null;
  }

  function updateAddButton(strip) {
    var btn = strip.querySelector('[data-strip-add]');
    if (btn) btn.disabled = !readyVariant(strip);
    var buy = strip.querySelector('[data-strip-buy]');
    if (buy) buy.disabled = !readyVariant(strip) || strip.classList.contains('is-buying');
    var rows = strip.querySelectorAll('.quick-strip__row').length;
    var picked = strip.querySelectorAll('.quick-strip__value.is-selected').length;
    var pct = rows ? Math.round((picked / rows) * 100) : 0;
    strip.style.setProperty('--qs-progress', pct + '%');
  }

  function reset(strip) {
    clearTimeout(strip.__qsTimer);
    strip.classList.remove('is-added');
    stopBuying(strip);
    Array.prototype.slice.call(strip.querySelectorAll('.quick-strip__value.is-selected')).forEach(function (b) {
      b.classList.remove('is-selected');
    });
    // Oct 8 2026: no default size; only a one-variant product keeps its single value selected
    if (getData(strip).variants.length === 1) {
      Array.prototype.slice.call(strip.querySelectorAll('[data-strip-default]:not([disabled])')).forEach(function (b) {
        b.classList.add('is-selected');
      });
    }
    refreshAvailability(strip);
    updateAddButton(strip);
    strip.querySelector('[data-strip-rows]').hidden = false;
    var msg = strip.querySelector('[data-strip-msg]');
    msg.hidden = true;
    msg.textContent = '';
  }

  function showMessage(strip, text) {
    strip.querySelector('[data-strip-rows]').hidden = true;
    var msg = strip.querySelector('[data-strip-msg]');
    msg.textContent = text;
    msg.hidden = false;
    clearTimeout(strip.__qsTimer);
    strip.__qsTimer = setTimeout(function () {
      reset(strip);
    }, RESET_DELAY);
  }

  function addToCart(strip, variantId) {
    var routes = (window.theme || {}).routes || {};
    var url = (routes.cart_add_url || '/cart/add') + '.js';
    var btn = strip.querySelector('[data-strip-add]');
    // Optimistic: show the check immediately; roll back to a message if the add fails
    if (btn) btn.disabled = true;
    strip.classList.add('is-added');
    clearTimeout(strip.__qsTimer);
    strip.__qsTimer = setTimeout(function () {
      strip.classList.remove('is-added');
      strip.__qsTimer = setTimeout(function () {
        reset(strip);
      }, 350);
    }, ADDED_DELAY);
    fetch(url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
      body: JSON.stringify({items: [{id: variantId, quantity: 1}]})
    })
      .then(function (r) {
        return r.json().then(function (json) {
          return {ok: r.ok, json: json};
        });
      })
      .then(function (res) {
        if (res.ok) {
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
        } else {
          strip.classList.remove('is-added');
          showMessage(strip, (res.json && res.json.description) || 'Sold out');
        }
      })
      .catch(function () {
        strip.classList.remove('is-added');
        showMessage(strip, 'Something went wrong. Try again');
      });
  }

  // Buy now: add the picked size, then go straight to checkout
  // (Shopify shows Shop Pay / Apple Pay / Google Pay at the top there)
  function stopBuying(strip) {
    strip.classList.remove('is-buying');
    var buy = strip.querySelector('[data-strip-buy]');
    if (buy) buy.textContent = 'Buy now';
  }

  function buyNow(strip, variantId) {
    var routes = (window.theme || {}).routes || {};
    var url = (routes.cart_add_url || '/cart/add') + '.js';
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var buy = strip.querySelector('[data-strip-buy]');
    clearTimeout(strip.__qsTimer);
    strip.classList.add('is-buying');
    if (buy) { buy.disabled = true; buy.textContent = 'Going to checkout'; }
    var add = strip.querySelector('[data-strip-add]');
    if (add) add.disabled = true;
    fetch(url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
      body: JSON.stringify({items: [{id: variantId, quantity: 1}]})
    })
      .then(function (r) {
        return r.json().then(function (json) {
          return {ok: r.ok, json: json};
        });
      })
      .then(function (res) {
        if (res.ok) {
          window.location.href = root + 'checkout';
        } else {
          stopBuying(strip);
          showMessage(strip, (res.json && res.json.description) || 'Sold out');
        }
      })
      .catch(function () {
        stopBuying(strip);
        showMessage(strip, 'Something went wrong. Try again');
      });
  }

  // Back button from checkout restores this page from memory: put Buy now back
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    Array.prototype.slice.call(document.querySelectorAll('[data-quick-strip].is-buying')).forEach(function (strip) {
      stopBuying(strip);
      updateAddButton(strip);
    });
  });

  // Grid adds (size strip, quick-add sheet) stay silent so shoppers keep browsing.
  // Product-page adds (main form, sticky bar) let the theme open the bag as usual.
  var lastAddSource = null;
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest && e.target.closest('[data-strip-add], [data-quick-add-btn], dialog[data-quick-add-modal] [data-add-to-cart], .product__page [data-add-to-cart], [data-add-to-cart-bar]');
    if (!b) return;
    lastAddSource = b.closest('.product__page, [data-add-to-cart-bar]') && !b.closest('dialog[data-quick-add-modal]') ? 'pdp' : 'grid';
  }, true);
  window.addEventListener('theme:product:added', function (e) {
    if (lastAddSource === 'pdp') { lastAddSource = null; return; }
    e.stopImmediatePropagation();
    e.stopPropagation();
    // (No extra cart refresh here: the theme's own add pipeline already rebuilt
    // the cart before this event, and a second rebuild made items flash.)
    // Close the quick-add sheet after a beat so shoppers land back on the collection
    setTimeout(function () {
      var dlg = document.querySelector('dialog[open][data-quick-add-modal]');
      if (!dlg) return;
      var closeBtn = dlg.querySelector('[data-quick-add-modal-close]');
      if (closeBtn) closeBtn.click();
      else if (typeof dlg.close === 'function') dlg.close();
    }, 700);
  }, true);

  // Express-checkout wallets in cart drawer/page.
  // Oct 7 2026: Shopify now decides which wallets show (Shop Pay, Apple Pay, Google Pay, PayPal...)
  // from Settings > Payments. The old filter here could not see inside Shopify's wallet element and
  // hid the whole row, so Apple Pay / Google Pay never appeared. This only undoes any leftover hiding.
  function pruneWallets() {
    Array.prototype.slice.call(document.querySelectorAll('.additional-checkout-buttons')).forEach(function (wrap) {
      if (wrap.style.display === 'none') wrap.style.display = '';
    });
  }
  var pruneTimer = null;
  var pruneObserver = new MutationObserver(function () {
    clearTimeout(pruneTimer);
    pruneTimer = setTimeout(pruneWallets, 100);
  });
  function startPrune() {
    pruneObserver.observe(document.body, {childList: true, subtree: true});
    pruneWallets();
  }
  if (document.body) startPrune();
  else document.addEventListener('DOMContentLoaded', startPrune);

  // Swatch "+N" count: force it to act as a product link (the theme's own
  // click handler otherwise intercepts it to expand the swatch row)
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var countLink = e.target.closest('a.product-item__swatches__count');
    if (!countLink) return;
    e.stopImmediatePropagation();
    e.stopPropagation();
    var href = countLink.getAttribute('href');
    if (href) window.location.href = href;
  }, true);

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var addBtn = e.target.closest('[data-strip-add]');
    var buyBtn = e.target.closest('[data-strip-buy]');
    var valueBtn = e.target.closest('[data-strip-opt]');
    if (!addBtn && !buyBtn && !valueBtn) return;
    var strip = (addBtn || buyBtn || valueBtn).closest('[data-quick-strip]');
    if (!strip) return;
    e.preventDefault();
    e.stopPropagation();
    if (strip.classList.contains('is-busy') || strip.classList.contains('is-buying')) return;
    if (buyBtn) {
      if (buyBtn.disabled) return;
      var buyVariant = readyVariant(strip);
      if (buyVariant) buyNow(strip, buyVariant.id);
      return;
    }
    if (addBtn) {
      if (addBtn.disabled) return;
      var variant = readyVariant(strip);
      if (variant) addToCart(strip, variant.id);
      return;
    }
    if (valueBtn.disabled) return;
    var i = parseInt(valueBtn.getAttribute('data-strip-opt'), 10);
    buttons(strip, i).forEach(function (b) {
      b.classList.remove('is-selected');
    });
    valueBtn.classList.add('is-selected');
    refreshAvailability(strip);
    updateAddButton(strip);
  }, true);

  // Oct 8 2026: the card markup still renders size S as selected; clear it once per strip (also for
  // cards loaded later by filters or infinite scroll) so nothing is picked until the shopper taps
  function clearDefault(strip) {
    if (!strip || strip.__qsClean) return;
    strip.__qsClean = true;
    if (getData(strip).variants.length === 1) return;
    Array.prototype.slice.call(strip.querySelectorAll('.quick-strip__value.is-selected')).forEach(function (b) { b.classList.remove('is-selected'); });
    refreshAvailability(strip);
    updateAddButton(strip);
  }
  function clearAll() { Array.prototype.forEach.call(document.querySelectorAll('[data-quick-strip]'), clearDefault); }
  ['mouseover', 'touchstart', 'focusin'].forEach(function (ev) {
    document.addEventListener(ev, function (e) {
      var card = e.target && e.target.closest && e.target.closest('.product-item__image'); if (!card) return;
      clearDefault(card.querySelector('[data-quick-strip]'));
    }, { capture: true, passive: true });
  });
  if (document.readyState !== 'loading') clearAll(); else document.addEventListener('DOMContentLoaded', clearAll);

  // Start fresh next time the pointer leaves the card (unless mid-add / showing the note)
  document.addEventListener('mouseout', function (e) {
    if (!e.target || !e.target.closest) return;
    var image = e.target.closest('.product-item__image');
    if (!image) return;
    if (e.relatedTarget && image.contains(e.relatedTarget)) return;
    var strip = image.querySelector('[data-quick-strip]');
    if (!strip || strip.classList.contains('is-busy') || strip.classList.contains('is-added') || strip.classList.contains('is-buying')) return;
    var msg = strip.querySelector('[data-strip-msg]');
    if (msg && !msg.hidden) return;
    var changed = getData(strip).variants.length > 1 && !!strip.querySelector('.quick-strip__value.is-selected');
    if (changed) reset(strip);
  });
})();
